import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ForgotPasswordPage — custom in-app password reset using Clerk's API.
 *
 * This keeps the user in our UI rather than redirecting to Clerk's hosted pages.
 * Clerk's <SignIn> component also has a "Forgot password?" link built-in, but
 * we implement it here for users who navigate directly to /forgot-password.
 *
 * Flow:
 *   1. User enters their email address.
 *   2. Clerk sends a one-time code to that address.
 *   3. User enters the code + their new password.
 *   4. Clerk validates and resets — user is now signed in.
 *   5. Navigate to /dashboard (useAuth will load the local profile).
 *
 * MFA:
 *   If the account has MFA enabled, Clerk may require an additional factor
 *   after password reset. The `needsMfa` state shows a simple code entry that
 *   calls signIn.attemptSecondFactor().
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSignIn } from '@clerk/clerk-react';
import { FormField, Input } from '../../components/ui/FormField';
import { AlertBanner } from '../../components/ui/AlertBanner';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
// ─── Step 1: email entry ──────────────────────────────────────────────────────
const emailSchema = z.object({
    email: z.string().email('Please enter a valid email address'),
});
// ─── Step 2: code + new password ─────────────────────────────────────────────
const resetSchema = z.object({
    code: z.string().min(1, 'Enter the code sent to your email'),
    newPassword: z.string()
        .min(8, 'Password must be at least 8 characters')
        .max(128),
    confirmPassword: z.string(),
}).refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
});
// ─── Component ────────────────────────────────────────────────────────────────
export function ForgotPasswordPage() {
    const { signIn, isLoaded } = useSignIn();
    const navigate = useNavigate();
    const [phase, setPhase] = useState('email');
    const [error, setError] = useState(null);
    // Step 1 form
    const emailForm = useForm({ resolver: zodResolver(emailSchema) });
    // Step 2 form
    const resetForm = useForm({ resolver: zodResolver(resetSchema) });
    // ── Step 1: request reset code ────────────────────────────────────────────
    const sendCode = async (data) => {
        if (!isLoaded || !signIn)
            return;
        setError(null);
        try {
            await signIn.create({
                strategy: 'reset_password_email_code',
                identifier: data.email,
            });
            setPhase('code');
        }
        catch (err) {
            const msg = err?.errors?.[0]?.longMessage ?? 'An error occurred. Please try again.';
            setError(msg);
        }
    };
    // ── Step 2: verify code + set new password ────────────────────────────────
    const resetPassword = async (data) => {
        if (!isLoaded || !signIn)
            return;
        setError(null);
        try {
            const result = await signIn.attemptFirstFactor({
                strategy: 'reset_password_email_code',
                code: data.code,
                password: data.newPassword,
            });
            if (result.status === 'complete') {
                // Password reset successful — Clerk has signed the user in
                navigate('/dashboard');
            }
            else {
                // Shouldn't normally reach here; surface to help debug
                setError(`Unexpected status: ${result.status}`);
            }
        }
        catch (err) {
            const msg = err?.errors?.[0]?.longMessage ?? 'Invalid code or password. Please try again.';
            setError(msg);
        }
    };
    // ── Done state ────────────────────────────────────────────────────────────
    if (phase === 'done') {
        return (_jsxs("div", { className: "card p-8 text-center", children: [_jsx("div", { className: "mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100", children: _jsx("svg", { className: "h-6 w-6 text-green-600", fill: "none", viewBox: "0 0 24 24", strokeWidth: "2", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M4.5 12.75l6 6 9-13.5" }) }) }), _jsx("h2", { className: "text-xl font-semibold text-gray-900 mb-2", children: "Password reset" }), _jsx("p", { className: "text-sm text-gray-500 mb-6", children: "Your password has been updated. You are now signed in." }), _jsx(Link, { to: "/dashboard", className: "btn-primary inline-flex items-center justify-center px-6 py-2", children: "Go to your cellar" })] }));
    }
    // ── Step 2: code + new password ───────────────────────────────────────────
    if (phase === 'code') {
        return (_jsxs("div", { className: "card p-8", children: [_jsx("h2", { className: "text-xl font-semibold text-gray-900 mb-2", children: "Check your inbox" }), _jsx("p", { className: "text-sm text-gray-500 mb-6", children: "We've sent a reset code to your email address. Enter it below along with your new password." }), error && (_jsx(AlertBanner, { type: "error", title: error, onDismiss: () => setError(null), className: "mb-4" })), _jsxs("form", { onSubmit: resetForm.handleSubmit(resetPassword), className: "space-y-4", children: [_jsx(FormField, { label: "Reset code", error: resetForm.formState.errors.code?.message, required: true, id: "code", children: _jsx(Input, { id: "code", type: "text", autoComplete: "one-time-code", placeholder: "6-digit code", error: Boolean(resetForm.formState.errors.code), ...resetForm.register('code') }) }), _jsx(FormField, { label: "New password", error: resetForm.formState.errors.newPassword?.message, required: true, id: "newPassword", hint: "At least 8 characters", children: _jsx(Input, { id: "newPassword", type: "password", autoComplete: "new-password", error: Boolean(resetForm.formState.errors.newPassword), ...resetForm.register('newPassword') }) }), _jsx(FormField, { label: "Confirm new password", error: resetForm.formState.errors.confirmPassword?.message, required: true, id: "confirmPassword", children: _jsx(Input, { id: "confirmPassword", type: "password", autoComplete: "new-password", error: Boolean(resetForm.formState.errors.confirmPassword), ...resetForm.register('confirmPassword') }) }), _jsx("button", { type: "submit", disabled: resetForm.formState.isSubmitting, className: "btn-primary w-full justify-center py-2.5", children: resetForm.formState.isSubmitting ? _jsx(LoadingSpinner, { size: "sm" }) : 'Reset password' })] }), _jsx("p", { className: "mt-4 text-center text-sm text-gray-500", children: _jsx("button", { type: "button", onClick: () => { setPhase('email'); setError(null); }, className: "text-wine-700 hover:text-wine-800 font-medium", children: "Use a different email" }) })] }));
    }
    // ── Step 1: email entry ───────────────────────────────────────────────────
    return (_jsxs("div", { className: "card p-8", children: [_jsx("h2", { className: "text-xl font-semibold text-gray-900 mb-2", children: "Reset your password" }), _jsx("p", { className: "text-sm text-gray-500 mb-6", children: "Enter your email address and we'll send you a one-time reset code." }), error && (_jsx(AlertBanner, { type: "error", title: error, onDismiss: () => setError(null), className: "mb-4" })), _jsxs("form", { onSubmit: emailForm.handleSubmit(sendCode), className: "space-y-4", children: [_jsx(FormField, { label: "Email address", error: emailForm.formState.errors.email?.message, required: true, id: "email", children: _jsx(Input, { id: "email", type: "email", autoComplete: "email", placeholder: "you@example.com", error: Boolean(emailForm.formState.errors.email), ...emailForm.register('email') }) }), _jsx("button", { type: "submit", disabled: emailForm.formState.isSubmitting || !isLoaded, className: "btn-primary w-full justify-center py-2.5", children: emailForm.formState.isSubmitting ? _jsx(LoadingSpinner, { size: "sm" }) : 'Send reset code' })] }), _jsx("p", { className: "mt-6 text-center text-sm text-gray-500", children: _jsx(Link, { to: "/login", className: "text-wine-700 hover:text-wine-800 font-medium", children: "Back to sign in" }) })] }));
}
